import { useMemo, useState } from "react";
import styled from "styled-components";
import { FaCheck, FaPlus, FaTrash } from "react-icons/fa6";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import MultiFileUploader from "../../../Componentes/Utils/MultiFileUploader";
import { formatCurrency } from "../../../Funcoes/formatCurrency";
import { formatBRLFromCents, parseCentsFromInput } from "../../../Funcoes/Money";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

const Title = styled.h3`
    margin: 0 0 8px 0;
    font-size: 18px;
    color: #263b4b;
`;

const Subtitle = styled.p`
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #555;
`;

const InfoRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 14px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f3f4f6;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #6b7280;
    text-transform: uppercase;
`;

const InfoValue = styled.span`
    font-size: 14px;
    color: #111827;
    word-break: break-word;
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin: 12px 0 16px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const SummaryCard = styled.div`
    padding: 10px 12px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-family: inherit;
    font-size: 14px;
    color: #1f2937;
`;

const SummaryLabel = styled.span`
    font-family: inherit;
    font-size: inherit;
    font-weight: 600;
    color: #6b7280;
`;

const SummaryValue = styled.span`
    font-family: inherit;
    font-size: inherit;
    font-weight: 400;
    color: inherit;
    word-break: break-word;
`;

const Hint = styled.p`
    margin: 0 0 12px 0;
    font-size: 13px;
    color: ${({ $ok }) => ($ok ? "#15803d" : "#b45309")};
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SectionLabel = styled.p`
    margin: 16px 0 8px 0;
    font-size: 12px;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const ItensTable = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ItemCard = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
    background: #fff;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ItemHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const ItemTitle = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: #334155;
`;

const ItemFields = styled.div`
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 10px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const FieldLabel = styled.label`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
`;

const TextInput = styled.input`
    height: 38px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    padding: 0 10px;
    font-size: 14px;
    color: #0f172a;
    box-sizing: border-box;

    &:disabled {
        background: #f1f5f9;
        color: #64748b;
    }
`;

const TextTextarea = styled.textarea`
    min-height: 38px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    padding: 8px 10px;
    font-size: 14px;
    color: #0f172a;
    resize: vertical;
    box-sizing: border-box;
    font-family: inherit;

    &::placeholder {
        color: #94a3b8;
        opacity: 1;
    }

    &:disabled {
        background: #f1f5f9;
        color: #64748b;
    }
`;

const AnexosExistentes = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const AnexoItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    a {
        color: #1d4ed8;
        font-size: 13px;
        text-decoration: none;
        word-break: break-all;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const IconButton = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    color: #b91c1c;

    &:hover {
        background: rgba(185, 28, 28, 0.1);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const ActionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 16px;
`;

const ActionButton = styled.button`
    flex: 1;
    min-width: 140px;
    height: 42px;
    border-radius: 10px;
    background-color: ${({ $variant }) =>
        $variant === "secondary"
            ? "#64748b"
            : $variant === "outline"
            ? "#fff"
            : "#4caf50"};
    color: ${({ $variant }) => ($variant === "outline" ? "#1e293b" : "#fff")};
    border: ${({ $variant }) => ($variant === "outline" ? "1px solid #cbd5e1" : "none")};
    font-weight: bold;
    font-size: 13px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        background-color: #c1c1c1;
        color: #fff;
        border: none;
        cursor: not-allowed;
    }
`;

const AddButton = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;
    padding: 8px 12px;
    border-radius: 8px;
    background: #2563eb;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    width: fit-content;

    &:hover {
        background: #1d4ed8;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const LogTabela = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    margin-top: 4px;

    th {
        text-align: left;
        padding: 8px 12px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #64748b;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
    }

    td {
        padding: 8px 12px;
        font-size: 13px;
        color: #1e293b;
        border-top: 1px solid #f1f5f9;
    }

    td.valor {
        text-align: right;
        white-space: nowrap;
    }
`;

const LogVazio = styled.p`
    margin: 4px 0 0 0;
    font-size: 13px;
    color: #64748b;
`;

const LogScroll = styled.div`
    max-height: 220px;
    overflow-y: auto;
`;

function formatMoney(value) {
    if (value == null || Number.isNaN(Number(value))) return "-";
    return `R$ ${formatCurrency(value)}`;
}

function formatDateTime(value) {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("pt-BR");
}

function toCents(value) {
    return Math.round(Number(value) * 100);
}

function createEmptyItem() {
    return {
        key: `new-${Date.now()}-${Math.random()}`,
        id: null,
        valorCents: 0,
        observacao: "",
        anexosExistentes: [],
        novosAnexos: [],
    };
}

function mapServerItens(itens) {
    if (!Array.isArray(itens) || itens.length === 0) {
        return [createEmptyItem()];
    }

    return itens.map((item, index) => ({
        key: item.id != null ? `id-${item.id}` : `idx-${index}`,
        id: item.id ?? null,
        // API passa valor em centavos (BIGINT)
        valorCents: Number(item.valor ?? 0),
        observacao: item.observacao || "",
        anexosExistentes: Array.isArray(item.anexos) ? item.anexos : [],
        novosAnexos: [],
    }));
}

const HistoricoLog = ({ logs }) => {
    if (!logs || logs.length === 0) {
        return <LogVazio>Nenhum registro de alteração ainda.</LogVazio>;
    }

    return (
        <LogScroll>
            <LogTabela>
                <thead>
                    <tr>
                        <th>Usuário</th>
                        <th>Data</th>
                        <th>Soma</th>
                    </tr>
                </thead>
                <tbody>
                    {logs.map((log) => (
                        <tr key={log.id}>
                            <td>{log.usuario || "-"}</td>
                            <td>{formatDateTime(log.created_at)}</td>
                            <td className="valor">{formatMoney(Number(log.valor_conciliado || 0) / 100)}</td>
                        </tr>
                    ))}
                </tbody>
            </LogTabela>
        </LogScroll>
    );
};

const ModalConciliacaoConta = ({ row, periodo, onSaved, readOnly = false }) => {
    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();

    const saldo = Number(row?.saldo ?? 0);
    const saldoCents = toCents(saldo);
    const inicialFechada = Boolean(row?.conciliacao?.conciliado);

    const [itens, setItens] = useState(() => mapServerItens(row?.conciliacao?.itens));
    const [removerAnexoIds, setRemoverAnexoIds] = useState([]);
    const [fechada, setFechada] = useState(inicialFechada);
    const [saving, setSaving] = useState(false);
    const [closing, setClosing] = useState(false);
    const [reopening, setReopening] = useState(false);

    const logs = row?.conciliacao?.logs ?? [];
    const preenchidoPor = row?.conciliacao?.preenchido_por;
    const preenchidoEm = row?.conciliacao?.preenchido_em;

    const podeEditar = !readOnly && !fechada;
    const busy = saving || closing || reopening;

    const somaCents = useMemo(
        () => itens.reduce((acc, item) => acc + Number(item.valorCents || 0), 0),
        [itens]
    );
    const diferencaCents = saldoCents - somaCents;
    const valoresBatem = diferencaCents === 0;

    const updateItem = (key, patch) => {
        setItens((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));
    };

    const handleAddItem = () => {
        setItens((prev) => [...prev, createEmptyItem()]);
    };

    const handleRemoveItem = (key) => {
        setItens((prev) => {
            if (prev.length <= 1) return prev;
            const removed = prev.find((item) => item.key === key);
            if (removed?.anexosExistentes?.length) {
                setRemoverAnexoIds((ids) => [
                    ...ids,
                    ...removed.anexosExistentes.map((a) => a.id).filter(Boolean),
                ]);
            }
            return prev.filter((item) => item.key !== key);
        });
    };

    const handleRemoveAnexoExistente = (itemKey, anexoId) => {
        setRemoverAnexoIds((prev) => [...prev, anexoId]);
        setItens((prev) =>
            prev.map((item) =>
                item.key === itemKey
                    ? {
                          ...item,
                          anexosExistentes: item.anexosExistentes.filter((a) => a.id !== anexoId),
                      }
                    : item
            )
        );
    };

    const buildFormData = () => {
        const formData = new FormData();
        formData.append("conta", row.conta);
        formData.append("ano", String(periodo.ano));
        formData.append("mes", String(Number(periodo.mes)));

        const payloadItens = itens.map((item) => ({
            id: item.id,
            valor: Number(item.valorCents || 0),
            observacao: item.observacao || "",
        }));
        formData.append("itens", JSON.stringify(payloadItens));

        if (removerAnexoIds.length > 0) {
            formData.append("remover_anexo_ids", JSON.stringify(removerAnexoIds));
        }

        itens.forEach((item, index) => {
            (item.novosAnexos || []).forEach((anexo) => {
                if (anexo?.file) {
                    formData.append(`files_${index}[]`, anexo.file);
                }
            });
        });

        return formData;
    };

    const applyServerItens = (serverItens) => {
        setItens(mapServerItens(serverItens));
        setRemoverAnexoIds([]);
    };

    const handleSave = async () => {
        if (!podeEditar || busy || !periodo || !row?.conta) return;

        setSaving(true);
        try {
            const { data } = await api.post(
                "/Painel/API/Contabilidade/Conciliacao/Save/index.php",
                buildFormData()
            );

            if (data?.success) {
                notifySuccess(data?.message || "Itens salvos.");
                applyServerItens(data.itens);
                setFechada(false);
                onSaved?.();
                return;
            }

            notifyError(data?.message || "Erro ao salvar itens.");
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao salvar itens.");
        } finally {
            setSaving(false);
        }
    };

    const handleFechar = async () => {
        if (!podeEditar || busy || !valoresBatem || !periodo || !row?.conta) return;

        setClosing(true);
        try {
            // Garante que o rascunho atual está persistido antes de fechar
            const saveRes = await api.post(
                "/Painel/API/Contabilidade/Conciliacao/Save/index.php",
                buildFormData()
            );

            if (!saveRes?.data?.success) {
                notifyError(saveRes?.data?.message || "Erro ao salvar antes de conciliar.");
                return;
            }

            applyServerItens(saveRes.data.itens);

            const { data } = await api.post(
                "/Painel/API/Contabilidade/Conciliacao/Fechar/index.php",
                {
                    conta: row.conta,
                    ano: periodo.ano,
                    mes: Number(periodo.mes),
                }
            );

            if (data?.success) {
                notifySuccess(data?.message || "Conciliação fechada.");
                setFechada(true);
                if (data.itens) applyServerItens(data.itens);
                onSaved?.();
                closeModal();
                return;
            }

            notifyError(data?.message || "Erro ao fechar conciliação.");
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao fechar conciliação.");
        } finally {
            setClosing(false);
        }
    };

    const handleReabrir = async () => {
        if (readOnly || busy || !fechada || !periodo || !row?.conta) return;

        setReopening(true);
        try {
            const { data } = await api.post(
                "/Painel/API/Contabilidade/Conciliacao/Reabrir/index.php",
                {
                    conta: row.conta,
                    ano: periodo.ano,
                    mes: Number(periodo.mes),
                }
            );

            if (data?.success) {
                notifySuccess(data?.message || "Conciliação reaberta.");
                setFechada(false);
                if (data.itens) applyServerItens(data.itens);
                onSaved?.();
                return;
            }

            notifyError(data?.message || "Erro ao reabrir conciliação.");
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao reabrir conciliação.");
        } finally {
            setReopening(false);
        }
    };

    return (
        <Overlay state changeState={closeModal}>
            <Modal header close={closeModal} size="lg">
                <Title>
                    {readOnly
                        ? "Detalhes da conciliação"
                        : fechada
                        ? "Conciliação fechada"
                        : "Conciliar conta"}
                </Title>
                <Subtitle>
                    {readOnly
                        ? "Você não é responsável por esta conta. Visualização somente leitura."
                        : fechada
                        ? "Conciliação fechada. Reabra para editar os itens."
                        : "Adicione um ou mais valores. Salve o rascunho a qualquer momento e concilie quando a soma for igual ao saldo."}
                </Subtitle>

                <InfoRow>
                    <InfoLabel>Conta</InfoLabel>
                    <InfoValue>
                        {row?.conta} — {row?.descricao || "-"}
                    </InfoValue>
                </InfoRow>

                {row?.conciliacao && (
                    <InfoRow>
                        <InfoLabel>Preenchido por</InfoLabel>
                        <InfoValue>
                            {preenchidoPor || "-"}
                            {preenchidoEm ? ` em ${formatDateTime(preenchidoEm)}` : ""}
                        </InfoValue>
                    </InfoRow>
                )}

                <SummaryGrid>
                    <SummaryCard>
                        <SummaryLabel>Saldo (Protheus)</SummaryLabel>
                        <SummaryValue>{formatMoney(saldo)}</SummaryValue>
                    </SummaryCard>
                    <SummaryCard>
                        <SummaryLabel>Soma dos itens</SummaryLabel>
                        <SummaryValue>{formatMoney(somaCents / 100)}</SummaryValue>
                    </SummaryCard>
                    <SummaryCard>
                        <SummaryLabel>Diferença</SummaryLabel>
                        <SummaryValue>{formatMoney(diferencaCents / 100)}</SummaryValue>
                    </SummaryCard>
                </SummaryGrid>

                <Hint $ok={valoresBatem}>
                    {valoresBatem ? (
                        <>
                            <FaCheck size={14} /> Soma confere com o saldo.
                        </>
                    ) : (
                        "A conciliação só pode ser fechada quando a soma for igual ao saldo."
                    )}
                </Hint>

                <SectionLabel>Itens</SectionLabel>
                <ItensTable>
                    {itens.map((item, index) => (
                        <ItemCard key={item.key}>
                            <ItemHeader>
                                <ItemTitle>Item {index + 1}</ItemTitle>
                                {podeEditar && (
                                    <IconButton
                                        type="button"
                                        title="Remover item"
                                        disabled={itens.length <= 1 || busy}
                                        onClick={() => handleRemoveItem(item.key)}
                                    >
                                        <FaTrash size={14} />
                                    </IconButton>
                                )}
                            </ItemHeader>

                            <ItemFields>
                                <Field>
                                    <FieldLabel>Valor</FieldLabel>
                                    <TextInput
                                        type="text"
                                        inputMode="numeric"
                                        disabled={!podeEditar || busy}
                                        value={formatBRLFromCents(item.valorCents)}
                                        onChange={(e) =>
                                            updateItem(item.key, {
                                                valorCents: parseCentsFromInput(e.target.value),
                                            })
                                        }
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel>Observações</FieldLabel>
                                    <TextTextarea
                                        disabled={!podeEditar || busy}
                                        value={item.observacao}
                                        placeholder="Observação do item"
                                        onChange={(e) =>
                                            updateItem(item.key, { observacao: e.target.value })
                                        }
                                    />
                                </Field>
                            </ItemFields>

                            {item.anexosExistentes.length > 0 && (
                                <>
                                    <FieldLabel>Anexos</FieldLabel>
                                    <AnexosExistentes>
                                        {item.anexosExistentes.map((anexo) => (
                                            <AnexoItem key={anexo.id}>
                                                <a
                                                    href={anexo.caminho}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {anexo.nome_original || "Arquivo"}
                                                </a>
                                                {podeEditar && (
                                                    <IconButton
                                                        type="button"
                                                        title="Remover anexo"
                                                        disabled={busy}
                                                        onClick={() =>
                                                            handleRemoveAnexoExistente(
                                                                item.key,
                                                                anexo.id
                                                            )
                                                        }
                                                    >
                                                        <FaTrash size={12} />
                                                    </IconButton>
                                                )}
                                            </AnexoItem>
                                        ))}
                                    </AnexosExistentes>
                                </>
                            )}

                            {podeEditar && (
                                <>
                                    <FieldLabel>Novos anexos</FieldLabel>
                                    <MultiFileUploader
                                        files={item.novosAnexos}
                                        setFiles={(updater) => {
                                            setItens((prev) =>
                                                prev.map((it) => {
                                                    if (it.key !== item.key) return it;
                                                    const next =
                                                        typeof updater === "function"
                                                            ? updater(it.novosAnexos)
                                                            : updater;
                                                    return { ...it, novosAnexos: next };
                                                })
                                            );
                                        }}
                                        maxFiles={0}
                                        maxSizeMB={20}
                                    />
                                </>
                            )}
                        </ItemCard>
                    ))}
                </ItensTable>

                {podeEditar && (
                    <AddButton type="button" onClick={handleAddItem} disabled={busy}>
                        <FaPlus size={12} /> Adicionar item
                    </AddButton>
                )}

                <ActionsRow>
                    {podeEditar && (
                        <>
                            <ActionButton
                                type="button"
                                $variant="outline"
                                disabled={busy}
                                onClick={handleSave}
                            >
                                {saving ? "SALVANDO…" : "SALVAR"}
                            </ActionButton>
                            <ActionButton
                                type="button"
                                disabled={busy || !valoresBatem}
                                onClick={handleFechar}
                            >
                                {closing ? "CONCILIANDO…" : "CONCILIAR"}
                            </ActionButton>
                        </>
                    )}

                    {!readOnly && fechada && (
                        <ActionButton
                            type="button"
                            $variant="secondary"
                            disabled={busy}
                            onClick={handleReabrir}
                        >
                            {reopening ? "REABRINDO…" : "REABRIR"}
                        </ActionButton>
                    )}
                </ActionsRow>

                <SectionLabel>Histórico de alterações</SectionLabel>
                <HistoricoLog logs={logs} />
            </Modal>
        </Overlay>
    );
};

export default ModalConciliacaoConta;
